import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  Stack,
} from '@mui/material';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { apiClient } from '../services/apiClient';
import MyCoupon from './MyCoupon';


const TIER_CONFIG = {
  BRONZE: {
    label: 'Bronze Voucher',
    color: '#D69A6C',
    gradient: 'linear-gradient(135deg, #D69A6C 0%, #A9673A 100%)',
    bgLight: 'rgba(214, 154, 108, 0.12)',
    border: 'rgba(214, 154, 108, 0.3)',
  },
  SILVER: {
    label: 'Silver Voucher',
    color: '#D8DEDF',
    gradient: 'linear-gradient(135deg, #D8DEDF 0%, #8F9A9C 100%)',
    bgLight: 'rgba(216, 222, 223, 0.12)',
    border: 'rgba(216, 222, 223, 0.3)',
  },
  GOLD: {
    label: 'Gold Voucher',
    color: '#E8C078',
    gradient: 'linear-gradient(135deg, #F0D18B 0%, #B9852E 100%)',
    bgLight: 'rgba(232, 192, 120, 0.12)',
    border: 'rgba(232, 192, 120, 0.3)',
  },
  PLATINUM: {
    label: 'Platinum Voucher',
    color: '#A3BE8C',
    gradient: 'linear-gradient(135deg, #E5E9F0 0%, #A3BE8C 100%)',
    bgLight: 'rgba(163, 190, 140, 0.12)',
    border: 'rgba(163, 190, 140, 0.3)',
  },
};

export default function PurchasedCoupons({ onNavigate }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(true);
  const [selectedTier, setSelectedTier] = useState('ALL');
  const [toastMessage, setToastMessage] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [selectedCouponForModal, setSelectedCouponForModal] = useState(null);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [hasRedeemedInSession, setHasRedeemedInSession] = useState(false);

  const handleRedeemClick = (coupon) => {
    setSelectedCouponForModal(coupon);
    setRedeemModalOpen(true);
  };

  const handleCouponRedeemed = (redeemedId) => {
    setHasRedeemedInSession(true);
    setCoupons((prev) => prev.filter((c) => c.couponId !== redeemedId && c.id !== redeemedId));
  };

  const handleCloseModal = () => {
    setRedeemModalOpen(false);
    setSelectedCouponForModal(null);
    if (isLoggedIn) {
      fetchPurchasedCoupons(page);
    }
  };

  const isLoggedIn = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));

  const fetchPurchasedCoupons = async (pageNumber = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/coupons/purchased?page=${pageNumber}&size=10`);
      if (response && response.success && response.data) {
        setCoupons(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalElements(response.data.totalElements || 0);
        setIsFirst(response.data.first ?? true);
        setIsLast(response.data.last ?? true);
        setPage(response.data.number || 0);
      } else {
        throw new Error(response?.message || 'Failed to fetch purchased coupons');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong while fetching purchased coupons.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchPurchasedCoupons(page);
    } else {
      setLoading(false);
    }
  }, [page, isLoggedIn]);

  const handleCopyCode = (couponId) => {
    const code = `ISORET-CPN-${couponId}`;
    navigator.clipboard?.writeText(code).catch(() => { });
    setToastMessage(`Coupon Code #${couponId} copied to clipboard!`);
    setToastOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const unredeemedCoupons = coupons.filter((c) => c.status !== 'REDEEMED');

  const filteredCoupons = selectedTier === 'ALL'
    ? unredeemedCoupons
    : unredeemedCoupons.filter((c) => c.tier?.toUpperCase() === selectedTier);


  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#0F2E2B',
          color: '#F5F1E6',
          py: { xs: 8, md: 12 },
          display: 'flex',
          alignItems: 'center',
          justify: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(700px 500px at 85% -10%, rgba(212,162,76,0.12), transparent 60%),
              radial-gradient(600px 500px at -10% 80%, rgba(47,143,118,0.16), transparent 60%)
            `,
            zIndex: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              borderRadius: '28px',
              backgroundColor: 'rgba(21, 58, 53, 0.7)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(212, 162, 76, 0.3)',
              textAlign: 'center',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: 'rgba(212, 162, 76, 0.15)',
                color: '#D4A24C',
                display: 'grid',
                placeItems: 'center',
                mx: 'auto',
                mb: 3,
                border: '1px solid rgba(212, 162, 76, 0.3)',
              }}
            >
              <LockIcon sx={{ fontSize: 36 }} />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                color: '#F5F1E6',
                fontSize: { xs: '24px', sm: '28px' },
                mb: 1.5,
              }}
            >
              My Coupons Restricted
            </Typography>

            <Typography
              sx={{
                color: 'rgba(245, 241, 230, 0.7)',
                fontSize: '15px',
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Please log in to your account to view your purchased vouchers, copy discount codes, and redeem store cashback.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => onNavigate && onNavigate('login')}
                startIcon={<PersonIcon />}
                sx={{
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  fontWeight: 700,
                  fontSize: '15px',
                  px: 4,
                  py: 1.4,
                  borderRadius: '14px',
                  '&:hover': {
                    backgroundColor: '#E8C078',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Sign In to View Coupons
              </Button>

              <Button
                variant="outlined"
                onClick={() => onNavigate && onNavigate('signup')}
                sx={{
                  borderColor: 'rgba(245, 241, 230, 0.3)',
                  color: '#F5F1E6',
                  fontWeight: 600,
                  fontSize: '15px',
                  px: 3,
                  py: 1.4,
                  borderRadius: '14px',
                  '&:hover': {
                    borderColor: '#D4A24C',
                    color: '#D4A24C',
                    backgroundColor: 'rgba(212, 162, 76, 0.08)',
                  },
                }}
              >
                Create Account
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#0F2E2B',
        color: '#F5F1E6',
        py: { xs: 5, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(700px 500px at 85% -10%, rgba(212,162,76,0.12), transparent 60%),
            radial-gradient(600px 500px at -10% 80%, rgba(47,143,118,0.16), transparent 60%)
          `,
          zIndex: 0,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Navigation Tabs Header */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ConfirmationNumberIcon />}
            sx={{
              backgroundColor: '#E8C078',
              color: '#0F2E2B',
              fontWeight: 700,
              borderRadius: '12px',
              px: 2.5,
              py: 1,
              '&:hover': { backgroundColor: '#F0D18B' }
            }}
          >
            Active Coupons
          </Button>
          <Button
            onClick={() => onNavigate && onNavigate('redeemed-coupons')}
            startIcon={<HistoryIcon />}
            sx={{
              color: 'rgba(245, 241, 230, 0.6)',
              borderRadius: '12px',
              px: 2.5,
              py: 1,
              border: '1px solid rgba(245, 241, 230, 0.1)',
              '&:hover': { color: '#F5F1E6', borderColor: 'rgba(245, 241, 230, 0.3)' }
            }}
          >
            Redeemed Coupons
          </Button>
        </Box>

        {/* Page Title Header */}
        <Box sx={{ textAlign: 'left', mb: 5 }}>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '13px',
              color: '#E8C078',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            My Digital Passes
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 700,
              fontSize: { xs: '30px', md: '42px' },
              color: '#F5F1E6',
              mb: 1.5,
            }}
          >
            Purchased Coupons
          </Typography>
          <Typography sx={{ color: 'rgba(245, 241, 230, 0.66)', fontSize: '16px', maxWidth: '650px' }}>
            Here are all your active purchased vouchers and coupon cards. Ready to be scratched or redeemed for assured cashback & exclusive brand rewards.
          </Typography>
        </Box>

        {!isLoggedIn ? (
          /* Not Logged In State */
          <Paper
            elevation={0}
            sx={{
              background: 'rgba(21, 58, 53, 0.5)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(245, 241, 230, 0.12)',
              borderRadius: '24px',
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              maxWidth: '540px',
              mx: 'auto',
              my: 6,
            }}
          >
            <LockIcon sx={{ fontSize: '48px', color: '#E8C078', mb: 2 }} />
            <Typography variant="h5" sx={{ fontFamily: '"Fraunces", serif', color: '#F5F1E6', fontWeight: 700, mb: 1.5 }}>
              Login Required
            </Typography>
            <Typography sx={{ color: 'rgba(245, 241, 230, 0.65)', fontSize: '15px', mb: 3 }}>
              Please log in to view your purchased digital coupons and voucher passes.
            </Typography>
            <Button
              variant="contained"
              onClick={() => onNavigate && onNavigate('login')}
              sx={{
                backgroundColor: '#E8C078',
                color: '#0F2E2B',
                fontWeight: 700,
                fontSize: '15px',
                px: 4,
                py: 1.2,
                borderRadius: '12px',
                '&:hover': { backgroundColor: '#F0D18B' },
              }}
            >
              Log In Now
            </Button>
          </Paper>
        ) : (
          <>
            {/* Filter Chips & Stats */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mb: 4,
                p: 2,
                borderRadius: '16px',
                background: 'rgba(21, 58, 53, 0.4)',
                border: '1px solid rgba(245, 241, 230, 0.08)',
              }}
            >
              {/* Filter Tiers */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['ALL', 'BRONZE', 'SILVER', 'GOLD'].map((tier) => (
                  <Chip
                    key={tier}
                    label={tier === 'ALL' ? 'All Coupons' : tier}
                    onClick={() => setSelectedTier(tier)}
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      backgroundColor: selectedTier === tier ? '#E8C078' : 'rgba(245, 241, 230, 0.06)',
                      color: selectedTier === tier ? '#0F2E2B' : 'rgba(245, 241, 230, 0.7)',
                      border: '1px solid',
                      borderColor: selectedTier === tier ? '#E8C078' : 'rgba(245, 241, 230, 0.12)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedTier === tier ? '#E8C078' : 'rgba(245, 241, 230, 0.12)',
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Total Count */}
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '13px', color: '#E8C078', fontWeight: 600 }}>
                Total Purchased: {totalElements}
              </Typography>
            </Box>

            {/* Main Content Area */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress sx={{ color: '#E8C078' }} />
              </Box>
            ) : error ? (
              <Paper
                elevation={0}
                sx={{
                  background: 'rgba(228, 87, 46, 0.1)',
                  border: '1px solid rgba(228, 87, 46, 0.3)',
                  borderRadius: '16px',
                  p: 4,
                  textAlign: 'center',
                  my: 4,
                }}
              >
                <Typography sx={{ color: '#E4572E', fontFamily: '"JetBrains Mono", monospace', mb: 2 }}>
                  ⚠️ Error loading coupons: {error}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => fetchPurchasedCoupons(page)}
                  sx={{ color: '#E8C078', borderColor: '#E8C078' }}
                >
                  Try Again
                </Button>
              </Paper>
            ) : filteredCoupons.length === 0 ? (
              /* Empty Coupons State */
              <Paper
                elevation={0}
                sx={{
                  background: 'rgba(21, 58, 53, 0.4)',
                  border: '1px solid rgba(245, 241, 230, 0.1)',
                  borderRadius: '24px',
                  p: { xs: 4, md: 6 },
                  textAlign: 'center',
                  my: 4,
                }}
              >
                <LocalActivityIcon sx={{ fontSize: '56px', color: 'rgba(245, 241, 230, 0.3)', mb: 2 }} />
                <Typography variant="h6" sx={{ fontFamily: '"Fraunces", serif', color: '#F5F1E6', mb: 1 }}>
                  No Purchased Coupons Found
                </Typography>
                <Typography sx={{ color: 'rgba(245, 241, 230, 0.5)', fontSize: '14.5px', mb: 3 }}>
                  {selectedTier !== 'ALL'
                    ? `You don't have any ${selectedTier} tier coupons currently purchased.`
                    : "You haven't bought any coupon cards yet. Explore our digital coupon tiers and start claiming assured cashback!"}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => onNavigate && onNavigate('home')}
                  sx={{
                    backgroundColor: '#E8C078',
                    color: '#0F2E2B',
                    fontWeight: 700,
                    borderRadius: '12px',
                    px: 3,
                    py: 1,
                  }}
                >
                  Explore Coupon Cards
                </Button>
              </Paper>
            ) : (
              /* Coupons Ticket Grid - 2 columns on large screen, 1 column on mobile */
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                  gap: 3,
                  mb: 5,
                }}
              >
                {filteredCoupons.map((coupon) => {
                  const tierKey = coupon.tier?.toUpperCase() || 'BRONZE';
                  const tierInfo = TIER_CONFIG[tierKey] || TIER_CONFIG.BRONZE;
                  const priceInRupees = coupon.pricePaise ? (coupon.pricePaise / 100).toFixed(0) : '0';

                  return (
                    <Paper
                      key={coupon.couponId}
                      elevation={0}
                      sx={{
                        background: 'rgba(21, 58, 53, 0.65)',
                        backdropFilter: 'blur(12px)',
                        border: `1px solid ${tierInfo.border}`,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 18px 40px rgba(0,0,0,0.45)`,
                          borderColor: tierInfo.color,
                        },
                      }}
                    >
                      {/* Left Side Voucher Stub (Gradient Brand Card) */}
                      <Box
                        sx={{
                          background: tierInfo.gradient,
                          color: '#0F2E2B',
                          width: { xs: '100%', sm: '190px' },
                          p: 2.5,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          position: 'relative',
                          flexShrink: 0,
                        }}
                      >
                        {/* Punch Hole effect for Ticket styling */}
                        <Box
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            position: 'absolute',
                            right: '-12px',
                            top: '-12px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#0F2E2B',
                            zIndex: 3,
                          }}
                        />
                        <Box
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            position: 'absolute',
                            right: '-12px',
                            bottom: '-12px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#0F2E2B',
                            zIndex: 3,
                          }}
                        />

                        {/* Top Tier Tag & ID */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Chip
                            label={coupon.tier}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(15, 46, 43, 0.22)',
                              color: '#0F2E2B',
                              fontWeight: 800,
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '11px',
                              letterSpacing: '0.08em',
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              opacity: 0.85,
                            }}
                          >
                            #CPN-{coupon.couponId}
                          </Typography>
                        </Box>

                        {/* Price display */}
                        <Box sx={{ my: 1 }}>
                          <Typography sx={{ fontSize: '10.5px', textTransform: 'uppercase', fontWeight: 600, opacity: 0.75 }}>
                            Voucher Value
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Fraunces", serif',
                              fontSize: '30px',
                              fontWeight: 800,
                              lineHeight: 1.1,
                            }}
                          >
                            ₹{priceInRupees}
                          </Typography>
                        </Box>

                        {/* Bottom Label */}
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, opacity: 0.8, mt: 0.5 }}>
                          Digital Coupon Pass
                        </Typography>
                      </Box>

                      {/* Perforated Vertical Line */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          width: '1px',
                          borderRight: '2px dashed rgba(245, 241, 230, 0.18)',
                          my: 2,
                        }}
                      />

                      {/* Right Side Voucher Details */}
                      <Box
                        sx={{
                          p: 2.5,
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        {/* Status Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: '15px !important', color: '#2F8F76 !important' }} />}
                              label={coupon.status}
                              sx={{
                                backgroundColor: 'rgba(47, 143, 118, 0.16)',
                                color: '#2F8F76',
                                border: '1px solid rgba(47, 143, 118, 0.3)',
                                fontWeight: 700,
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: '11.5px',
                              }}
                            />
                            <Typography sx={{ color: 'rgba(245, 241, 230, 0.5)', fontSize: '12px', fontFamily: '"JetBrains Mono", monospace' }}>
                              Unscratched
                            </Typography>
                          </Box>
                        </Box>

                        {/* Dates Info */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: 1.2,
                            p: 1.5,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(15, 46, 43, 0.5)',
                            border: '1px solid rgba(245, 241, 230, 0.06)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ color: '#E8C078', fontSize: '16px' }} />
                            <Box>
                              <Typography sx={{ fontSize: '10px', color: 'rgba(245, 241, 230, 0.45)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Purchased On
                              </Typography>
                              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#F5F1E6' }}>
                                {formatDate(coupon.purchasedAt)}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ color: '#E4572E', fontSize: '16px' }} />
                            <Box>
                              <Typography sx={{ fontSize: '10px', color: 'rgba(245, 241, 230, 0.45)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Expires On
                              </Typography>
                              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#F5F1E6' }}>
                                {formatDate(coupon.expiresAt)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Actions Foot */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 0.5 }}>
                          <Typography sx={{ fontSize: '11.5px', color: 'rgba(245, 241, 230, 0.5)', fontStyle: 'italic' }}>
                            ✓ Active for {coupon.tier} deals
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() => handleRedeemClick(coupon)}
                            sx={{
                              backgroundColor: tierInfo.color,
                              color: '#0F2E2B',
                              fontWeight: 700,
                              fontSize: '12.5px',
                              borderRadius: '10px',
                              px: 2.5,
                              py: 0.7,
                              '&:hover': {
                                opacity: 0.9,
                              },
                            }}
                          >
                            Redeem Now
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4 }}>
                <IconButton
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={isFirst}
                  sx={{
                    color: '#F5F1E6',
                    border: '1px solid rgba(245, 241, 230, 0.15)',
                    '&.Mui-disabled': { color: 'rgba(245, 241, 230, 0.2)' },
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '14px', color: '#E8C078', fontWeight: 600 }}>
                  Page {page + 1} of {totalPages}
                </Typography>
                <IconButton
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={isLast}
                  sx={{
                    color: '#F5F1E6',
                    border: '1px solid rgba(245, 241, 230, 0.15)',
                    '&.Mui-disabled': { color: 'rgba(245, 241, 230, 0.2)' },
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            )}
          </>
        )}

        {/* Snackbar Toast notification */}
        <Snackbar
          open={toastOpen}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" sx={{ width: '100%', background: '#153A35', color: '#E8C078', border: '1px solid #E8C078' }}>
            {toastMessage}
          </Alert>
        </Snackbar>

        {/* Redeem ScratchCard Modal Popup */}
        <Dialog
          open={redeemModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'transparent',
              boxShadow: 'none',
              overflow: 'visible',
            },
          }}
        >
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            {selectedCouponForModal && (
              <MyCoupon
                coupon={selectedCouponForModal}
                onClose={handleCloseModal}
                onRedeemed={handleCouponRedeemed}
                isModal={true}
              />
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
}

